/*
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { Detector } from '../detectors/base.js';

/**
 * Plugin metadata for discovery and validation
 */
export interface PluginMetadata {
  /**
   * Plugin name (e.g., "omnirun-plugin-rust")
   */
  name: string;

  /**
   * Plugin version (semver)
   */
  version: string;

  /**
   * Plugin description
   */
  description: string;

  /**
   * Plugin author
   */
  author?: string;

  /**
   * Minimum OmniRun version required
   */
  omnirunVersion?: string;

  /**
   * Keywords for discovery
   */
  keywords?: string[];
}

/**
 * Plugin interface for external detector packages
 */
export interface DetectorPlugin extends Detector {
  /**
   * Plugin metadata
   */
  metadata: PluginMetadata;

  /**
   * Optional initialization hook
   */
  init?(): Promise<void> | void;

  /**
   * Optional cleanup hook
   */
  cleanup?(): Promise<void> | void;
}

/**
 * Plugin configuration
 */
export interface PluginConfig {
  /**
   * Enable/disable the plugin
   */
  enabled: boolean;

  /**
   * Plugin-specific configuration
   */
  config?: Record<string, unknown>;
}

/**
 * OmniRun configuration file (.omnirunrc.json)
 */
export interface OmniRunConfig {
  /**
   * Default mode (dev, build, test, etc.)
   */
  defaultMode?: string;

  /**
   * Custom modes and their commands
   */
  modes?: Record<string, { command: string; args: string[] }>;

  /**
   * Plugin configurations
   */
  plugins?: Record<string, PluginConfig>;

  /**
   * Additional plugin search paths
   */
  pluginPaths?: string[];

  /**
   * Auto-fix behavior
   */
  autoFix?: boolean;

  /**
   * Verbose mode
   */
  verbose?: boolean;
}

/**
 * Plugin loading error
 */
export class PluginLoadError extends Error {
  constructor(
    public pluginName: string,
    message: string,
    public cause?: Error
  ) {
    super(`Plugin "${pluginName}": ${message}`);
    this.name = 'PluginLoadError';
  }
}

/**
 * Plugin validation error
 */
export class PluginValidationError extends Error {
  constructor(
    public pluginName: string,
    message: string
  ) {
    super(`Plugin "${pluginName}" validation failed: ${message}`);
    this.name = 'PluginValidationError';
  }
}

